import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import JobFormDrawer from '../components/jobs/JobFormDrawer';
import RescheduleMenu from '../components/jobs/RescheduleMenu';
import FieldDoneDialog from '../components/jobs/FieldDoneDialog';
import WeekGrid, { readDrag } from '../components/planner/WeekGrid';
import JobPeek from '../components/planner/JobPeek';
import { StatusPill } from '../components/common/ui';
import JobService from '../services/JobService';
import { errorMessage } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import UserService from '../services/UserService';
import { addDays, fmtDate, fmtDateWith, startOfWeek, toIsoDate } from '../utils/format';
import { fieldStepOf } from '../utils/workflows';
import { usePageRefresh } from '../components/layout/pullToRefresh';

/** Initials of the people assigned to a job. */
function Crew({ people, warnEmpty }) {
    const { t } = useTranslation();
    if (!people?.length) {
        return warnEmpty ? (
            <Tooltip title={t('jobs.noAssignees')}>
                <PersonAddAlt1OutlinedIcon sx={{ fontSize: 17, color: '#D97706', ml: 'auto' }} />
            </Tooltip>
        ) : null;
    }
    return (
        <Tooltip title={people.map(p => `${p.firstName} ${p.lastName}`).join(', ')}>
            <Box sx={{ display: 'flex', ml: 'auto' }}>
                {people.slice(0, 3).map((p, i) => (
                    <Box key={p.id} sx={{
                        width: 22, height: 22, borderRadius: '50%', bgcolor: '#E8F0F5', color: '#427898', border: '2px solid #fff',
                        fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', ml: i ? -0.4 : 0,
                    }}>{`${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`.toUpperCase()}</Box>
                ))}
                {people.length > 3 && <Typography sx={{ fontSize: 11, color: '#94A3B8', ml: 0.5 }}>+{people.length - 3}</Typography>}
            </Box>
        </Tooltip>
    );
}

/** A job in the lists above the grid (late, not planned yet): drag it onto the week grid, or use the reschedule menu. */
function JobCard({ job, onMove, onFieldDone, onOpen, showDate }) {
    const { t } = useTranslation();
    const fieldStep = fieldStepOf(job.category);
    return (
        <Box
            draggable
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(job.id)); e.dataTransfer.effectAllowed = 'move'; }}
            onClick={(e) => onOpen(job, e.currentTarget)}
            sx={{
                bgcolor: '#fff', border: '1px solid #E2E8F0', borderLeft: `4px solid ${job.category?.color || '#CBD5E1'}`,
                borderRadius: '10px', p: 1.25, cursor: 'grab', transition: 'box-shadow 0.15s', width: 230, flexShrink: 0,
                '&:hover': { boxShadow: '0 8px 20px -12px rgba(30,58,82,0.35)' }, '&:active': { cursor: 'grabbing' },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: '#0F172A', lineHeight: 1.3, wordBreak: 'break-word' }}>{job.title}</Typography>
                    <Typography noWrap sx={{ fontSize: 11.5, color: '#64748B', mt: 0.25 }}>
                        {job.client?.name}{showDate && job.scheduledDate ? ` · ${fmtDate(job.scheduledDate)}` : ''}
                    </Typography>
                </Box>
                <RescheduleMenu job={job} onMove={onMove} />
            </Box>
            <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center' }}><StatusPill status={job.status} size="sm" /><Crew people={job.assignees} warnEmpty /></Box>
            {fieldStep && (
                <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleOutlineIcon />}
                        onClick={(e) => { e.stopPropagation(); onFieldDone(job, fieldStep); }} sx={{ mt: 1, py: 0.25, fontSize: 12 }}>
                    {t('planner.markFieldDone')}
                </Button>
            )}
        </Box>
    );
}

/** Horizontal list above the grid; the "not planned yet" one is also a drop target that removes the planned date. */
function SideList({ title, subtitle, jobs, tone, onDropJob, emptyText, children }) {
    const [over, setOver] = useState(false);
    const red = tone === 'red';
    return (
        <Box onDragOver={onDropJob ? (e) => { e.preventDefault(); setOver(true); } : undefined}
             onDragLeave={() => setOver(false)}
             onDrop={onDropJob ? (e) => { e.preventDefault(); setOver(false); onDropJob(e); } : undefined}
             sx={{
                 bgcolor: over ? 'rgba(66,120,152,0.08)' : (red ? 'rgba(239,68,68,0.04)' : 'rgba(248,250,252,0.85)'),
                 border: '1px solid', borderColor: over ? '#427898' : (red ? 'rgba(239,68,68,0.25)' : '#E2E8F0'),
                 borderRadius: '14px', p: 1.25, minWidth: 0,
             }}>
            <Box sx={{ px: 0.5, pb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: red ? '#DC2626' : '#1a2332' }}>{title}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{subtitle}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, alignItems: 'flex-start' }}>
                {children}
                {!jobs.length && emptyText && <Typography sx={{ fontSize: 12, color: '#CBD5E1', my: 0.5, px: 0.5 }}>{emptyText}</Typography>}
            </Box>
        </Box>
    );
}

export default function PlannerPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAdmin, user } = useAuth();
    // Whose jobs to show: '' = everyone, 'me', or a person's id. Employees start with their own jobs.
    const [person, setPerson] = useState(() => {
        try { return localStorage.getItem('SD_PLANNER_PERSON') ?? (isAdmin ? '' : 'me'); } catch { return isAdmin ? '' : 'me'; }
    });
    const [people, setPeople] = useState([]);
    useEffect(() => { UserService.directory().then(setPeople).catch(() => {}); }, []);
    const changePerson = (v) => {
        setPerson(v);
        try { localStorage.setItem('SD_PLANNER_PERSON', v); } catch { /* ignore */ }
    };
    const assignee = person === 'me' ? user?.id : (person || undefined);
    const { showToast } = useToast();
    const [anchor, setAnchor] = useState(() => new Date());
    const [data, setData] = useState({ scheduled: [], unscheduled: [], overdue: [] });
    const [loading, setLoading] = useState(true);
    const [drawer, setDrawer] = useState({ open: false, defaults: null });
    const [fieldDone, setFieldDone] = useState(null); // { job, step }
    const [peek, setPeek] = useState(null); // { job, anchorEl }

    const todayIso = toIsoDate(new Date());
    const range = useMemo(() => {
        const start = startOfWeek(anchor);
        return { from: toIsoDate(start), to: toIsoDate(addDays(start, 6)), days: [...Array(7)].map((_, i) => addDays(start, i)) };
    }, [anchor]);

    const load = useCallback(() => {
        setLoading(true);
        return JobService.planner(range.from, range.to, assignee)
            .then(setData)
            .catch(() => showToast(t('common.loadError'), 'error'))
            .finally(() => setLoading(false));
    }, [range.from, range.to, assignee, showToast, t]);
    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    const allJobs = useMemo(() => [...data.scheduled, ...data.unscheduled, ...(data.overdue || [])], [data]);

    /** Date (null = not planned) and optional time slot; Undo in the toast puts the old slot back. */
    const schedule = async (job, date, start = null, end = null) => {
        const prev = [job.scheduledDate || null, job.scheduledStart || null, job.scheduledEnd || null];
        if (prev[0] === (date || null) && prev[1] === start && prev[2] === end) return;
        try {
            await JobService.setSchedule(job.id, date, start, end);
            const when = date ? `${fmtDateWith(new Date(date + 'T00:00'), { weekday: 'short', day: 'numeric', month: 'short' })}${start ? `, ${start}${end ? '–' + end : ''}` : ''}` : null;
            showToast(when ? t('planner.moved', { date: when }) : t('planner.unscheduled'), 'success', {
                action: { label: t('workflow.undo'), onClick: () => JobService.setSchedule(job.id, ...prev).then(load).catch(() => {}) },
            });
            load();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        }
    };
    const scheduleById = (id, date, start, end) => {
        const job = allJobs.find(j => j.id === id);
        if (job) schedule(job, date, start, end);
    };

    const openAdd = (date = null, start = null, end = null) =>
        setDrawer({ open: true, defaults: date ? { scheduledDate: date, scheduledStart: start || '', scheduledEnd: end || '' } : null });

    const weekLabel = `${fmtDateWith(range.days[0], { day: 'numeric', month: 'short' })} – ${fmtDateWith(range.days[6], { day: 'numeric', month: 'short', year: 'numeric' })}`;
    const card = (j, showDate) => (
        <JobCard key={j.id} job={j} showDate={showDate} onMove={(job, d) => schedule(job, d)} onFieldDone={(job, step) => setFieldDone({ job, step })}
                 onOpen={(job, el) => setPeek({ job, anchorEl: el })} />
    );

    return (
        <Layout>
            <PageHeader
                title={t('planner.title')}
                subtitle={t('planner.subtitle')}
                action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => openAdd()}>{t('jobs.new')}</Button>}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => setAnchor(a => addDays(a, -7))} aria-label={t('planner.prevWeek')}><ChevronLeftIcon /></IconButton>
                    <Button size="small" variant="outlined" onClick={() => setAnchor(new Date())} sx={{ whiteSpace: 'nowrap' }}>{t('planner.thisWeek')}</Button>
                    <IconButton size="small" onClick={() => setAnchor(a => addDays(a, 7))} aria-label={t('planner.nextWeek')}><ChevronRightIcon /></IconButton>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a2332', ml: 1 }}>{weekLabel}</Typography>
                </Box>
                <TextField select size="small" value={String(person)} onChange={(e) => changePerson(e.target.value)}
                           sx={{ minWidth: 180, bgcolor: '#fff' }} slotProps={{ select: { displayEmpty: true }, htmlInput: { 'aria-label': t('planner.whose') } }}>
                    <MenuItem value="">{t('planner.everyone')}</MenuItem>
                    <MenuItem value="me">{t('planner.mine')}</MenuItem>
                    {people.filter(p => p.id !== user?.id).map(p => <MenuItem key={p.id} value={String(p.id)}>{p.firstName} {p.lastName}</MenuItem>)}
                </TextField>
                {/* Printable / shareable plan for one day (tomorrow by default, prepared the evening before). */}
                <Button size="small" variant="outlined" startIcon={<ListAltOutlinedIcon />} sx={{ bgcolor: '#fff', whiteSpace: 'nowrap' }}
                        onClick={() => navigate(`/planner/day?date=${toIsoDate(addDays(new Date(), 1))}${assignee ? `&person=${assignee}` : ''}`)}>
                    {t('dayPlan.title')}
                </Button>
                {loading && <CircularProgress size={18} />}
                <Typography sx={{ ml: 'auto', fontSize: 12, color: '#94A3B8', display: { xs: 'none', md: 'block' } }}>{t('planner.gridHint')}</Typography>
            </Box>

            {/* Late and not-yet-planned jobs sit above the grid so the week gets the full width. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: data.overdue?.length ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr' }, gap: 2, mb: 2 }}>
                    {data.overdue?.length > 0 && (
                        <SideList tone="red" title={t('planner.overdueTitle', { count: data.overdue.length })} subtitle={t('planner.overdueSub')} jobs={data.overdue}>
                            {data.overdue.map(j => card(j, true))}
                        </SideList>
                    )}
                    <SideList title={t('planner.unscheduledTitle')} subtitle={t('planner.unscheduledSub')} jobs={data.unscheduled}
                              emptyText={t('planner.noUnscheduled')}
                              onDropJob={(e) => { const { id } = readDrag(e); if (id) scheduleById(id, null); }}>
                        {data.unscheduled.map(j => card(j, false))}
                    </SideList>
            </Box>
            <WeekGrid days={range.days} jobs={data.scheduled} todayIso={todayIso}
                      onSchedule={scheduleById} onCreate={openAdd} onOpen={(job, el) => setPeek({ job, anchorEl: el })} />

            <JobFormDrawer open={drawer.open} defaults={drawer.defaults} onClose={() => setDrawer({ open: false, defaults: null })}
                           onSaved={() => { setDrawer({ open: false, defaults: null }); load(); }} />
            <JobPeek job={peek?.job} anchorEl={peek?.anchorEl} onClose={() => setPeek(null)}
                     onFieldDone={(job, step) => setFieldDone({ job, step })} onSchedule={schedule} />
            <FieldDoneDialog job={fieldDone?.job} step={fieldDone?.step} onClose={() => setFieldDone(null)}
                             onDone={() => { setFieldDone(null); load(); }} />
        </Layout>
    );
}
